import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cn } from '@shared/lib/cn'
import { AvatarSize } from './types'

export interface AvatarProps {
  src?: string | null
  name?: string | null
  size?: AvatarSize
  className?: string
}

const sizeClasses: Record<AvatarSize, string> = {
  [AvatarSize.sm]: 'h-8 w-8 text-xs',
  [AvatarSize.md]: 'h-10 w-10 text-sm',
  [AvatarSize.lg]: 'h-14 w-14 text-base',
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?'
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
}

const FALLBACK_PALETTE = [
  'bg-orange-primary/20 text-orange-primary',
  'bg-blue-500/20 text-blue-600',
  'bg-green-500/20 text-green-600',
  'bg-violet-500/20 text-violet-600',
  'bg-red-500/20 text-red-600',
  'bg-amber-500/20 text-amber-600',
  'bg-teal-500/20 text-teal-600',
  'bg-pink-500/20 text-pink-600',
] as const

function pickFallbackColor(name: string | null | undefined): string {
  if (!name) return FALLBACK_PALETTE[0]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0
  }
  return FALLBACK_PALETTE[Math.abs(hash) % FALLBACK_PALETTE.length]
}

export function Avatar({ src, name, size = AvatarSize.md, className }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        'relative flex shrink-0 overflow-hidden rounded-full',
        sizeClasses[size],
        className
      )}
    >
      {src && (
        <AvatarPrimitive.Image src={src} alt={name ?? ''} className="h-full w-full object-cover" />
      )}
      <AvatarPrimitive.Fallback
        className={cn(
          'flex h-full w-full items-center justify-center rounded-full font-semibold',
          pickFallbackColor(name)
        )}
        delayMs={src ? 300 : 0}
      >
        {getInitials(name)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}
